/**
 * `resolutionDice` — project a combat **resolution** (a recorded `ResolvedStep[]`) onto the
 * physical dice the overlay throws. This is the bridge that makes a *real* in-app roll — an
 * attack, a save, a structure/stress table check — tumble on the global dice surface.
 *
 * **What actually arrives client-side is steps, not a `RollResult`.** The authority resolves a
 * roll once (`@lancer-vtt/rules`'s `resolve`) and records it as flat, self-describing
 * `ResolvedStep`s (`to_hit`/`damage`/`save`/`table_check`/…) — *those* are what travel the wire
 * (`encounter_change.resolution`) and land in the session resolution feed
 * (`RemoteEncounterController.resolutions`). So this module reads the recorded **outcome fields**
 * the steps carry and emits one {@link DiceSpec} per die, each landing on its recorded value:
 *
 *   - **`to_hit` / `save`** — the base **d20** lands on the recorded `d20`; a nonzero
 *     `accuracy`/difficulty adds one **d6** landing on `|accuracy|` (the single *kept* accuracy
 *     die — the step records the net contribution, not the dropped pool, so we show the one die
 *     it kept).
 *   - **`damage`** — one die per recorded kept face in `dice[]`. The damage step records the die's
 *     `sides` when a real pool was rolled, so a d3 weapon renders a d3 solid; a step without
 *     `sides` falls back to **d6** (Lancer's common damage die).
 *   - **`table_check`** — the structure/overheating **d6 take-lowest** pool: one d6 per face in
 *     `dice[]`, with the chosen (lowest) die flagged kept and the rest dimmed, mirroring the
 *     mechanic. These dice *are* d6 by rule, so the shape is exact.
 *   - **`link`** — a chain link's random pick (Aggression's d3 over its neighbours,
 *     `decisions/0210`) lands on the recorded face, when the pool's size is a die the overlay has a
 *     solid for. A pick over 2, 5 or 7 candidates throws nothing; its face is in the card's
 *     RANDOM row.
 *
 * Every other step (`hp_delta`, `status_add`, `move`, …) carries no roll and contributes no dice.
 *
 * Pure + framework-free (no `three`/`cannon-es`/DOM): the only dependency is the `ResolvedStep`
 * **type** from `@lancer-vtt/domain`. It reads data that already crosses the wire, so it needs no
 * reducer or wire-protocol change — that's what keeps the dice module isolated.
 */
import type { ResolvedStep } from "@lancer-vtt/domain";
import type { DiceSpec } from "./rollChoreography";

/** The die sizes the overlay has a solid for (`geometryForSides`); a random pick over another pool size throws no die. */
const PICK_SIDES: ReadonlySet<number> = new Set([3, 4, 6, 8, 10, 12, 20]);

/** Default die shape for a recorded damage face — the step doesn't carry `sides`; d6 is Lancer's common damage die. */
const DAMAGE_SIDES_DEFAULT = 6;

/**
 * Project a resolution's recorded steps onto the dice to throw, in step order (a to-hit's d20
 * tumbles alongside the damage dice it rolled). Returns `[]` for a resolution with no rolls
 * (a pure move / status application) — the caller skips the overlay entirely.
 */
export function diceSpecsFromSteps(steps: readonly ResolvedStep[]): DiceSpec[] {
  const specs: DiceSpec[] = [];
  for (const step of steps) {
    switch (step.op) {
      case "to_hit":
      case "save": {
        specs.push({ sides: 20, value: step.d20, kept: true });
        if (step.accuracy !== 0) {
          specs.push({ sides: 6, value: Math.abs(step.accuracy), kept: true });
        }
        break;
      }
      case "damage": {
        // The step records the die size (`sides`) when a real pool was rolled; fall back to d6
        // (Lancer's common damage die) when it doesn't.
        const sides = step.sides ?? DAMAGE_SIDES_DEFAULT;
        for (const face of step.dice) {
          specs.push({ sides, value: face, kept: true });
        }
        break;
      }
      case "table_check": {
        // d6 take-lowest: the chosen (lowest) die is kept; the rest are shown dimmed. Flag the
        // FIRST die equal to `lowest` as the kept one (ties are interchangeable).
        let keptLowest = false;
        for (const face of step.dice) {
          const isKept = !keptLowest && face === step.lowest;
          if (isKept) keptLowest = true;
          specs.push({ sides: 6, value: face, kept: isKept });
        }
        break;
      }
      case "link": {
        const pick = step.pick;
        if (pick?.roll !== undefined && PICK_SIDES.has(pick.from.length)) specs.push({ sides: pick.from.length, value: pick.roll, kept: true });
        break;
      }
      default:
        // hp_delta / heat_delta / structure_delta / status_add / move / … carry no roll.
        break;
    }
  }
  return specs;
}

/**
 * A terse, display-only witness of a resolution for the overlay readout — the roll totals the
 * steps record, never recomputed. Returns `null` when nothing roll-bearing happened (so the
 * panel stays hidden). e.g. `to-hit 18 hit`, `to-hit 18 hit · dmg 7`, `overheat ⚅2`.
 */
export function formatStepsSummary(steps: readonly ResolvedStep[]): string | null {
  const parts: string[] = [];
  for (const step of steps) {
    switch (step.op) {
      case "to_hit":
        parts.push(`to-hit ${step.total} ${step.result}`);
        break;
      case "save":
        parts.push(`${step.against} save ${step.total} ${step.result}`);
        break;
      case "damage":
        parts.push(`dmg ${step.final}`);
        break;
      case "table_check":
        parts.push(`${step.kind === "overheating" ? "overheat" : "structure"} ⚅${step.lowest}`);
        break;
      default:
        break;
    }
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}
